import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const AvailabilityGrid = ({ availMap, maxSlots, hourRange }:
  { availMap: Record<string, any>; maxSlots: number;
    hourRange?: { start: number; len: number; exclusive: boolean } }) => {
  const [offset, setOffset] = useState(0);
  const base = new Date();
  base.setDate(1);
  base.setMonth(base.getMonth() + offset);
  const year = base.getFullYear();
  const month = base.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const cellFor = (day: number) => {
    const d = new Date(year, month, day);
    const info = availMap[iso(d)];
    if (d < today || !info) return { cls: "bg-white/[0.03] text-gray-700", label: "unavailable" };
    if (hourRange && info.hours && info.excl) {
      let blocked = 0, used = 0;
      for (let h = hourRange.start; h < hourRange.start + hourRange.len; h++) {
        const excl = !!info.excl[h];
        const cnt = info.hours[h] || 0;
        if (excl || cnt >= maxSlots || (hourRange.exclusive && cnt > 0)) blocked++;
        if (cnt > 0 || excl) used++;
      }
      if (blocked > 0)
        return { cls: "bg-red-500/25 text-red-300 border border-red-500/30", label: "booked" };
      if (used > 0)
        return { cls: "bg-amber-500/20 text-amber-300 border border-amber-500/25", label: "partial" };
      return { cls: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/20", label: "open" };
    }
    if (info.booked_exclusive || info.standard_count >= maxSlots)
      return { cls: "bg-red-500/25 text-red-300 border border-red-500/30", label: "booked" };
    if (info.standard_count > 0)
      return { cls: "bg-amber-500/20 text-amber-300 border border-amber-500/25", label: "partial" };
    return { cls: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/20", label: "open" };
  };

  return (
    <div data-testid="availability-grid" className="bg-[#0d1117] border border-white/10 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="font-heading font-bold text-white text-sm">Availability — {MONTHS[month]} {year}</p>
        <div className="flex gap-1.5">
          <button type="button" onClick={() => setOffset(offset - 1)} disabled={offset <= 0}
            data-testid="grid-prev-month"
            className="h-7 w-7 rounded-md border border-white/15 flex items-center justify-center text-gray-300 hover:border-[#00E5FF]/50 disabled:opacity-30 transition-colors">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => setOffset(offset + 1)} disabled={offset >= 5}
            data-testid="grid-next-month"
            className="h-7 w-7 rounded-md border border-white/15 flex items-center justify-center text-gray-300 hover:border-[#00E5FF]/50 disabled:opacity-30 transition-colors">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {DOW.map((d) => (
          <span key={d} className="font-mono text-[9px] uppercase text-gray-600 py-1">{d}</span>
        ))}
        {Array.from({ length: firstDow }).map((_, i) => <span key={`e${i}`} />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const c = cellFor(i + 1);
          return (
            <span key={i} data-availability={c.label}
              className={`rounded font-mono text-[11px] py-1.5 ${c.cls}`}>
              {i + 1}
            </span>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3 font-mono text-[9px] uppercase tracking-widest text-gray-500">
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-500/40" /> Open</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-amber-500/40" /> Partially booked</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-red-500/40" /> Fully booked / exclusive</span>
      </div>
    </div>
  );
};
